import { Commit } from 'vuex'

import EnrollForm from '@/models/enroll-form'
import ErrorEntities from '@/models/error-entities'
import PaymentAmountOptions from '@/models/payment-amount-options'
import PaymentModalSteps from '@/models/payment-modal-steps'
import PaymentPresetTypes from '@/models/payment-preset-types'
import PendingPayment from '@/models/pending-payment'
import Recipient from '@/models/recipient'

import axios from '@/axios-module'
import notification from '@/notification'
import { PaymentMutationTypes } from '@/store/modules/payments'

export enum PaymentModalActionTypes {
  Enroll = '[PaymentModal] Enroll',
  GetPaymentCode = '[PaymentModal] GetPaymentCode',
}

export enum PaymentModalMutationTypes {
  Open = '[PaymentModal] Open',
  Close = '[PaymentModal] Close',
  SetRecipientIsChosen = '[PaymentModal] SetRecipientIsChosen',
  SetStep = '[PaymentModal] SetStep',
  SetRecipientAmount = '[PaymentModal] SetRecipientAmount',
  SetPaymentAmountOption = '[PaymentModal] SetPaymentAmountOption',
  SetPaymentPresetType = '[PaymentModal] SetPaymentPresetType',
  EnrollStart = '[PaymentModal] EnrollStart',
  EnrollSuccess = '[PaymentModal] EnrollSuccess',
  EnrollError = '[PaymentModal] EnrollError',
  ClearForm = '[PaymentModal] ClearForm',
  SetRecipientErrors = '[PaymentModal] SetRecipientErrors',
  UpdateTermsAccepted = '[PaymentModal] UpdateTermsAccepted',
  UpdateEnrollTabInfo = '[PaymentModal] UpdateEnrollTabInfo',
  GetPaymentCodeSuccess = '[PaymentModal] GetPaymentCodeSuccess',
  GetPaymentCodeError = '[PaymentModal] GetPaymentCodeError',
  Reset = '[PaymentModal] Reset',
}

export interface State {
  [key: string]: any
  errors: ErrorEntities
  recipients: { [id: number]: Recipient }
  currentStep: PaymentModalSteps
  paymentPresetType: PaymentPresetTypes
  paymentAmountOption: PaymentAmountOptions
  paymentCode: string
  paymentBeingProcessed: boolean
  paymentSuccessful: boolean
  termsAccepted: boolean
  cardholderName: string
  cardAliasName: string
  saveCard: boolean
  creditCardId: number | null
  completedPaymentId: number | null
}

function initialState(): State {
  return {
    errors: {},
    recipients: {}, // TODO: think about moving it to another store
    currentStep: PaymentModalSteps.Recipients,
    paymentPresetType: PaymentPresetTypes.None,
    paymentAmountOption: PaymentAmountOptions.Remaining,
    paymentCode: '',
    paymentBeingProcessed: false,
    paymentSuccessful: false,
    termsAccepted: false,
    cardholderName: '',
    cardAliasName: '',
    saveCard: false,
    creditCardId: null,
    completedPaymentId: null,
  }
}

export default {
  state: initialState,
  getters: {
    getEnrollTabCardInfo(state: State) {
      const { cardholderName, cardAliasName, saveCard, creditCardId } = state
      return { cardholderName, cardAliasName, saveCard, creditCardId }
    },
    getCompletedPaymentId(state: State) {
      return state.completedPaymentId
    },
  },
  actions: {
    async [PaymentModalActionTypes.Enroll](
      { commit }: { commit: Commit },
      enrollForm: EnrollForm,
    ) {
      try {
        let response = await axios.post(
          `/customer/bookings/${enrollForm.enquiryId}/payments`,
          { payment: enrollForm },
        )
        if (enrollForm.stripe && response.data.requiresAction) {
          const result = await enrollForm.stripe.handleCardAction(
            response.data.paymentIntentClientSecret,
          )
          if (result.error) {
            const message =
              result.error.message || 'Payment was declined by Stripe!'
            notification(message, 'error')
            return commit(
              PaymentModalMutationTypes.EnrollError,
              result.error.message,
            )
          }
          enrollForm.stripePaymentIntent =
            result.paymentIntent && result.paymentIntent.id

          response = await axios.post(
            `/customer/bookings/${enrollForm.enquiryId}/payments`,
            { payment: enrollForm },
          )
        }
        commit(PaymentMutationTypes.GetSuccess, response.data.payment)
        return commit(
          PaymentModalMutationTypes.EnrollSuccess,
          response.data.payment.id,
        )
      } catch ({ response }) {
        const message =
          (response.data && response.data.msg) || 'Payment was declined!'
        notification(message, 'error')
        return commit(PaymentModalMutationTypes.EnrollError, response.data)
      }
    },
    async [PaymentModalActionTypes.GetPaymentCode](
      { commit }: { commit: Commit },
      enrollForm: EnrollForm,
    ) {
      try {
        const response = await axios.post(
          `/customer/bookings/${enrollForm.enquiryId}/pending_payments`,
          { pendingPayment: enrollForm },
        )

        commit(PaymentModalMutationTypes.GetPaymentCodeSuccess, response.data)
      } catch (error) {
        commit(PaymentModalMutationTypes.GetPaymentCodeError)
      }
    },
  },
  mutations: {
    [PaymentModalMutationTypes.Open](state: State) {
      state.errors = {}
    },
    [PaymentModalMutationTypes.SetRecipientIsChosen](
      state: State,
      { recipientId, isChosen }: { recipientId: number; isChosen: boolean },
    ) {
      const recipient = {
        ...state.recipients[recipientId],
        id: recipientId,
        isChosen,
      }

      state.recipients = { ...state.recipients, [recipientId]: recipient }
    },
    [PaymentModalMutationTypes.SetStep](state: State, step: PaymentModalSteps) {
      state.currentStep = step
    },
    [PaymentModalMutationTypes.SetRecipientAmount](
      state: State,
      {
        recipientId,
        amountCents,
      }: { recipientId: number; amountCents: number },
    ) {
      const recipient = {
        ...state.recipients[recipientId],
        amount: Math.round(amountCents),
      }
      state.recipients = { ...state.recipients, [recipientId]: recipient }
    },
    [PaymentModalMutationTypes.SetPaymentAmountOption](
      state: State,
      paymentAmountOption: PaymentAmountOptions,
    ) {
      state.paymentAmountOption = paymentAmountOption
    },
    [PaymentModalMutationTypes.SetPaymentPresetType](
      state: State,
      amountType: PaymentPresetTypes,
    ) {
      state.paymentPresetType = amountType
    },
    [PaymentModalMutationTypes.EnrollStart](state: State) {
      state.paymentBeingProcessed = true
    },
    [PaymentModalMutationTypes.EnrollSuccess](state: State, paymentId: number) {
      state.completedPaymentId = paymentId
      state.currentStep = PaymentModalSteps.Success
      state.termsAccepted = false
      state.paymentBeingProcessed = false
    },
    [PaymentModalMutationTypes.EnrollError](
      state: State,
      errors: ErrorEntities,
    ) {
      state.paymentBeingProcessed = false
      state.errors = errors
    },
    [PaymentModalMutationTypes.ClearForm](state: State) {
      state.recipients = {}
      state.errors = {}
      state.paymentAmountOption = PaymentAmountOptions.Remaining
      state.paymentPresetType = PaymentPresetTypes.None
      state.currentStep = PaymentModalSteps.Recipients
      state.cardholderName = ''
      state.cardAliasName = ''
      state.saveCard = false
      state.creditCardId = null
    },
    [PaymentModalMutationTypes.SetRecipientErrors](
      state: State,
      { recipientId, errors }: { recipientId: number; errors: ErrorEntities },
    ) {
      const recipient = { ...state.recipients[recipientId], errors }
      state.recipients = { ...state.recipients, [recipientId]: recipient }
    },
    [PaymentModalMutationTypes.UpdateTermsAccepted](
      state: State,
      termsAccepted: boolean,
    ) {
      state.termsAccepted = termsAccepted
    },
    [PaymentModalMutationTypes.UpdateEnrollTabInfo](
      state: State,
      changes: { name: string; value: any },
    ) {
      // TODO: check this, this might be an incorrect usage of `in`
      if (changes.name in state) {
        state[changes.name] = changes.value
      }
    },
    [PaymentModalMutationTypes.GetPaymentCodeSuccess](
      state: State,
      pendingPayment: PendingPayment,
    ) {
      state.paymentCode = pendingPayment.code
    },
    [PaymentModalMutationTypes.GetPaymentCodeError](state: State) {
      state.paymentCode = 'Error occured, please reload the page.'
    },
    [PaymentModalMutationTypes.Reset](state: State) {
      Object.assign(state, initialState())
    },
  },
}
